import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeftRight, Plus, Pencil } from "lucide-react";

export default function DpConversions() {
  const { can } = useAuth();
  const manage = can("digital_printing.material.conversion.manage");
  const [items, setItems] = useState([]);
  const [rows, setRows] = useState(null);
  const [filterItem, setFilterItem] = useState("all");
  const [dlg, setDlg] = useState(null); // {mode:'create'|'edit', conv?}

  const loadItems = useCallback(() => {
    api.get("/digital-printing/material/items").then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, []);
  const load = useCallback(() => {
    setRows(null);
    const params = filterItem !== "all" ? { item_id: filterItem } : {};
    api.get("/digital-printing/material/conversions", { params }).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [filterItem]);

  useEffect(() => { loadItems(); }, [loadItems]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (c) => {
    try {
      await api.post(`/digital-printing/material/conversions/${c.id}/${c.active ? "deactivate" : "activate"}`);
      toast.success(c.active ? "Konversi dinonaktifkan" : "Konversi diaktifkan"); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="dp-conversions-page">
      <PageHeader title="Konversi Satuan Material (UOM)" subtitle="Konversi item-scoped: Base Stock Unit ← Usage/Pack Unit. Contoh: ML ← LITER = 1000, GRAM ← KG = 1000." />

      <div className="flex flex-wrap items-center gap-3">
        <div className="w-72">
          <Select value={filterItem} onValueChange={setFilterItem}>
            <SelectTrigger data-testid="conv-filter-item"><SelectValue placeholder="Semua item" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua item</SelectItem>
              {items.map((it) => <SelectItem key={it.id} value={it.id}>{it.code ? `[${it.code}] ` : ""}{it.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {manage && <Button onClick={() => setDlg({ mode: "create" })} data-testid="conv-new-btn" className="bg-indigo-600 hover:bg-indigo-500"><Plus className="mr-1 h-4 w-4" />Konversi Baru</Button>}
      </div>

      {rows === null ? <Loading /> : rows.length === 0 ? (
        <EmptyState icon={ArrowLeftRight} title="Belum ada konversi" description="Buat konversi item-scoped, mis. ML ← LITER = 1000." />
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm" data-testid="conv-table">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">Item</th><th className="px-4 py-3">Kode</th>
                <th className="px-4 py-3">Base Stock Unit</th><th className="px-4 py-3">Usage/Pack Unit</th>
                <th className="px-4 py-3 text-right">Base Qty / Usage Unit</th>
                <th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className="border-t border-slate-100" data-testid={`conv-row-${c.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-700">{c.item_name || c.item_id}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{c.item_code || "-"}</td>
                  <td className="px-4 py-3">{c.base_stock_unit}</td>
                  <td className="px-4 py-3">{c.usage_or_pack_unit}</td>
                  <td className="px-4 py-3 text-right font-semibold" data-testid={`conv-factor-${c.id}`}>
                    {c.base_qty_per_usage_unit}
                    <span className="ml-1 text-[10px] font-normal text-slate-400">1 {c.usage_or_pack_unit} = {c.base_qty_per_usage_unit} {c.base_stock_unit}</span>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={c.active ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-100 text-slate-500"} data-testid={`conv-status-${c.id}`}>{c.active ? "Active" : "Inactive"}</Badge>
                    {c.legacy && <span className="ml-2 text-[10px] text-amber-600">legacy</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {manage && (
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => setDlg({ mode: "edit", conv: c })} data-testid={`conv-edit-${c.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="outline" onClick={() => toggle(c)} data-testid={`conv-toggle-${c.id}`}>{c.active ? "Nonaktifkan" : "Aktifkan"}</Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {dlg && <ConvDialog dlg={dlg} items={items} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); load(); }} />}
    </div>
  );
}

function ConvDialog({ dlg, items, onClose, onSaved }) {
  const isEdit = dlg.mode === "edit";
  const c = dlg.conv;
  const [itemId, setItemId] = useState(c?.item_id || "");
  const [usageUnit, setUsageUnit] = useState(c?.usage_or_pack_unit || "");
  const [factor, setFactor] = useState(c?.base_qty_per_usage_unit || "");
  const [notes, setNotes] = useState(c?.notes || "");
  const [saving, setSaving] = useState(false);
  const item = items.find((x) => x.id === itemId);
  const baseUnit = isEdit ? c.base_stock_unit : (item?.base_stock_unit || item?.unit || "");

  const save = async () => {
    if (!itemId) return toast.error("Pilih item");
    if (!isEdit && !usageUnit.trim()) return toast.error("Isi Usage/Pack Unit");
    if (!(Number(factor) > 0)) return toast.error("Base Qty per Usage Unit harus > 0");
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/digital-printing/material/conversions/${c.id}`, { base_qty_per_usage_unit: Number(factor), notes });
      } else {
        await api.post("/digital-printing/material/conversions", {
          item_id: itemId, base_stock_unit: baseUnit.toUpperCase(),
          usage_or_pack_unit: usageUnit.trim().toUpperCase(), base_qty_per_usage_unit: Number(factor), notes,
        });
      }
      toast.success(isEdit ? "Konversi diperbarui" : "Konversi dibuat"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="conv-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Konversi" : "Konversi Satuan Baru"}</DialogTitle>
          <DialogDescription>Item-scoped. Base Qty per Usage Unit = jumlah base stock unit per 1 usage/pack unit (mis. 1 LITER = 1000 ML → 1000).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-500">Item</label>
            <Select value={itemId} onValueChange={setItemId} disabled={isEdit}>
              <SelectTrigger className="mt-1" data-testid="conv-item"><SelectValue placeholder="Pilih item" /></SelectTrigger>
              <SelectContent>{items.map((it) => <SelectItem key={it.id} value={it.id}>{it.code ? `[${it.code}] ` : ""}{it.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-slate-500">Base Stock Unit</label><Input className="mt-1" value={baseUnit} readOnly data-testid="conv-base-unit" /></div>
            <div><label className="text-xs text-slate-500">Usage/Pack Unit</label><Input className="mt-1 uppercase" value={usageUnit} disabled={isEdit} placeholder="mis. LITER / KG / ROLL" onChange={(e) => setUsageUnit(e.target.value)} data-testid="conv-usage-unit" /></div>
          </div>
          <div><label className="text-xs text-slate-500">Base Qty per Usage Unit</label><Input className="mt-1" type="number" min="0" value={factor} placeholder="1000" onChange={(e) => setFactor(e.target.value)} data-testid="conv-factor-input" />
            {baseUnit && usageUnit && Number(factor) > 0 && <p className="mt-1 text-[11px] text-slate-400">1 {usageUnit.toUpperCase()} = {factor} {baseUnit.toUpperCase()}</p>}
          </div>
          <div><label className="text-xs text-slate-500">Catatan (opsional)</label><Input className="mt-1" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="conv-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={saving} data-testid="conv-save" className="bg-indigo-600 hover:bg-indigo-500">{saving ? "..." : "Simpan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
